import { useState, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { IconStateProvider } from '../icons/fromLucide'
import { LOOP } from '../tokens/motion'
import { cn } from '../lib/cn'
import { titleCaseNode } from '../lib/titleCase'

/**
 * The labelled button. Always a pill. Five ranks and no more:
 *
 *   primary    the one thing this surface wants you to do
 *   secondary  a real action, equal to its neighbours
 *   ghost      an action that should not compete
 *   danger     loses data or cannot be undone. Outlined, never filled.
 *   positive   a consequential yes — the clock-in, danger's mirror.
 *              Outlined, never filled.
 *
 * A plain-text label reads in title case — "New Sheet", "Write a Note".
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'positive'
type Size = 'xs' | 'sm' | 'md' | 'lg'

const VARIANT: Record<Variant, string> = {
  primary:
    'border border-transparent bg-primary text-primary-foreground shadow-chip hover:bg-primary/90 active:bg-primary active:shadow-none [&_.wf-icon]:text-primary-foreground/80',
  secondary: 'border bg-surface text-foreground shadow-chip hover:bg-tint active:bg-selected active:shadow-none',
  ghost: 'border border-transparent text-muted-foreground hover:bg-tint hover:text-foreground active:bg-selected',
  danger: 'border border-danger/35 text-danger hover:bg-danger/10 active:bg-danger/15 [&_.wf-icon]:text-danger',
  positive: 'border border-positive/35 text-positive hover:bg-positive/10 active:bg-positive/15 [&_.wf-icon]:text-positive',
}

const SIZE: Record<Size, string> = {
  xs: 'h-[var(--control-xs)] gap-1 px-2.5 text-caption',
  sm: 'h-[var(--control-sm)] gap-1.5 px-3.5 text-caption',
  md: 'h-[var(--control-md)] gap-2 px-4 text-ui',
  lg: 'h-[var(--control-lg)] gap-2 px-5 text-ui',
}

export function Button({
  onClick,
  onBlur,
  variant = 'secondary',
  size = 'md',
  disabled = false,
  busy = false,
  type = 'button',
  icon,
  fullWidth = false,
  className,
  children,
  'aria-label': ariaLabel,
}: {
  /** A fuller name than the visible words, for a button that shrinks to its icon. */
  'aria-label'?: string
  onClick?: () => void
  onBlur?: () => void
  variant?: Variant
  size?: Size
  disabled?: boolean
  /** An action is in flight: a spinner, and no further presses. */
  busy?: boolean
  type?: 'button' | 'submit'
  icon?: ReactNode
  fullWidth?: boolean
  className?: string
  children: ReactNode
}) {
  const [hover, setHover] = useState(false)
  const inert = disabled || busy

  return (
    <button
      type={type}
      onClick={onClick}
      onBlur={onBlur}
      disabled={inert}
      aria-busy={busy || undefined}
      aria-label={ariaLabel}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full font-medium leading-none whitespace-nowrap',
        'transition-[color,background-color,border-color,box-shadow] duration-fast',
        'disabled:pointer-events-none disabled:opacity-45',
        SIZE[size],
        VARIANT[variant],
        fullWidth && 'w-full',
        className,
      )}
    >
      {/* The label holds its place while busy, so nothing beside it moves. */}
      <span className={cn('inline-flex items-center gap-[inherit]', busy && 'opacity-0')}>
        <IconStateProvider state={hover && !inert ? 'hover' : 'rest'}>{icon}</IconStateProvider>
        {titleCaseNode(children)}
      </span>
      {busy ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <motion.span
            aria-hidden
            className="size-3.5 rounded-full border-[1.5px] border-current border-t-transparent opacity-70"
            animate={{ rotate: 360 }}
            transition={LOOP.spin}
          />
        </span>
      ) : null}
    </button>
  )
}
